import * as React from 'react';
import { Users } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { useProfile } from '../context/ProfileContext';
import { useStartedTraining } from '../context/StartedTrainingContext';
import { useLocale } from '../context/LocaleContext';
import {
  subscribePartnerInvites,
  PARTNER_INVITE_REFRESH_EVENT,
  PARTNER_INVITE_SHOW_EVENT,
  type PartnerInvitePayload,
} from '../lib/partnerSession';
import { API_BASE } from '../lib/apiBase';
import { authFetch, authFetchJSON } from '../lib/authFetch';
import { Button } from './ui/Button';

/**
 * Shows a Join / Decline prompt for "I Go You Go" partner invites.
 *
 * Two sources: the realtime broadcast (instant, but only while the app is open)
 * and the stored invite fetched on open/focus, which is what makes an invite
 * survive the guest's phone being locked when it was sent. Answering resolves
 * the stored row so it stops prompting on every launch.
 */

/** The stored row carries the same fields as the broadcast, plus its own id. */
type StoredInvite = PartnerInvitePayload & { id?: string };

export function PartnerInvitePrompt() {
  const { session } = useAuth();
  const { profile } = useProfile();
  const { startTraining, started } = useStartedTraining();
  const { t } = useLocale();

  const userId = session?.user?.id ?? '';
  const selfName = (profile?.username || session?.user?.email || 'Partner') as string;
  const [invite, setInvite] = React.useState<StoredInvite | null>(null);

  const fetchPending = React.useCallback(async (): Promise<StoredInvite[]> => {
    const res = await authFetchJSON<StoredInvite[]>(`${API_BASE}/api/partner-invites/pending`);
    return res.ok && Array.isArray(res.data) ? res.data : [];
  }, []);

  React.useEffect(() => {
    if (!userId) return;
    const unsub = subscribePartnerInvites(userId, (payload) => {
      // Show the broadcast immediately — it is the whole point of the live path.
      setInvite(payload);
      // The broadcast carries no invite id, so answering it could not resolve
      // the stored row and the invite would linger in the bell until it expired.
      // Look the row up by session and fold its id in; the modal is already on
      // screen either way, so a slow or failed lookup costs nothing.
      void fetchPending().then((rows) => {
        const match = rows.find((r) => r.sessionId === payload.sessionId);
        if (match?.id) {
          setInvite((current) =>
            current && current.sessionId === payload.sessionId ? { ...current, id: match.id } : current
          );
        }
        window.dispatchEvent(new Event(PARTNER_INVITE_REFRESH_EVENT));
      });
    });
    return () => unsub();
  }, [userId, fetchPending]);

  // Pending invites sent while this device was closed. Re-checked on focus so
  // an invite that arrives while the app sits in the background still shows.
  React.useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async () => {
      const rows = await fetchPending();
      if (cancelled) return;
      const newest = rows.find((r) => !r.id || !dismissedIds.current.has(r.id));
      if (!newest) return;
      // Don't clobber a live broadcast the user is already looking at.
      setInvite((current) => current ?? { ...newest });
    };

    void load();
    const onFocus = () => { void load(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [userId, fetchPending]);

  // Invites dismissed by tapping outside stay pending on the server — the user
  // hasn't answered. Remembering them here just stops the prompt reappearing on
  // every window focus; the bell lists it meanwhile, and it will be waiting
  // again next launch.
  const dismissedIds = React.useRef<Set<string>>(new Set());

  // Re-opened from the Header bell. Clearing the dismissal is the point: the
  // user is explicitly asking to see an invite they previously tapped past.
  React.useEffect(() => {
    const onShow = (e: Event) => {
      const inv = (e as CustomEvent).detail as StoredInvite | undefined;
      if (!inv?.sessionId || !inv?.training) return;
      if (inv.id) dismissedIds.current.delete(inv.id);
      setInvite(inv);
    };
    window.addEventListener(PARTNER_INVITE_SHOW_EVENT, onShow as EventListener);
    return () => window.removeEventListener(PARTNER_INVITE_SHOW_EVENT, onShow as EventListener);
  }, []);

  /** Resolve the stored row (if this invite came from one) so it stops prompting. */
  const respond = React.useCallback((inv: StoredInvite | null, status: 'accepted' | 'declined') => {
    if (!inv?.id) return;
    void authFetch(`${API_BASE}/api/partner-invites/${inv.id}/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    })
      // Answered, so drop it from the bell instead of leaving a stale offer.
      .then(() => window.dispatchEvent(new Event(PARTNER_INVITE_REFRESH_EVENT)))
      .catch(() => { /* best effort — expiry will clear it anyway */ });
  }, []);

  /** Tapped outside: no answer given, so leave the row pending. */
  const dismiss = React.useCallback(() => {
    setInvite((current) => {
      if (current?.id) dismissedIds.current.add(current.id);
      return null;
    });
  }, []);

  const decline = () => {
    respond(invite, 'declined');
    setInvite(null);
  };

  // Never interrupt an already-running session with an invite.
  if (!invite || started) return null;

  const join = () => {
    const inv = invite;
    setInvite(null);
    respond(inv, 'accepted');
    startTraining(inv.training, {
      sessionId: inv.sessionId,
      role: 'guest',
      selfUserId: userId,
      selfName,
      partnerName: inv.hostName,
    });
  };

  return (
    <div className="fixed inset-0 z-[10000] grid place-items-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 shadow-xl p-5 text-center">
        <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-brand-100 dark:bg-brand-900/40 grid place-items-center text-brand-600 dark:text-brand-400">
          <Users size={24} />
        </div>
        <div className="text-[11px] font-bold uppercase tracking-widest text-brand-600 dark:text-brand-400 mb-1">
          {t('I Go You Go', 'I Go You Go')}
        </div>
        <div className="font-semibold text-base">
          {invite.hostName} {t('invited you to a partner workout', 'invited you to a partner workout')}
        </div>
        {invite.trainingTitle && <div className="text-sm opacity-60 mt-1">{invite.trainingTitle}</div>}
        <div className="mt-5 flex gap-2">
          <Button variant="ghost" className="flex-1 justify-center" onClick={decline}>
            {t('Decline', 'Decline')}
          </Button>
          <Button variant="primary" className="flex-1 justify-center" onClick={join}>
            {t('Join', 'Join')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PartnerInvitePrompt;
